/**
 * Utility functions for handling different video platforms (YouTube, Vimeo)
 */

/**
 * Detect whether a URL or ID belongs to YouTube or Vimeo
 * @param {string} urlOrId - URL or video ID
 * @returns {string} 'youtube' or 'vimeo'
 */
export const detectPlatform = (urlOrId) => {
  if (typeof urlOrId === 'string') {
    const urlLower = urlOrId.toLowerCase();
    if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) {
      return 'youtube';
    } else if (urlLower.includes('vimeo.com')) {
      return 'vimeo';
    }
    // If it's just an ID, make an educated guess
    // Vimeo IDs can be numeric or include privacy hashes (e.g., "123456789/abcdef123")
    if (urlOrId.match(/^\d+$/) || urlOrId.match(/^\d+\/[a-f0-9]+$/)) {
      return 'vimeo';
    } else {
      return 'youtube'; // Default to YouTube for alphanumeric IDs
    }
  }
  return 'youtube'; // Default fallback
};

/**
 * Generate video URL based on platform and video ID
 * @param {string} videoId - Video ID (may include privacy hash for Vimeo)
 * @param {string} platform - Platform ('youtube' or 'vimeo')
 * @returns {string} Video URL
 */
export const getVideoUrl = (videoId, platform = 'youtube') => {
  if (platform === 'vimeo') {
    return `https://vimeo.com/${videoId}`;
  } else {
    return `https://www.youtube.com/watch?v=${videoId}`;
  }
};

/**
 * Generate video URL with timestamp based on platform
 * @param {string} videoId - Video ID (may include privacy hash for Vimeo)
 * @param {number} timestamp - Timestamp in seconds
 * @param {string} platform - Platform ('youtube' or 'vimeo')
 * @returns {string} Video URL with timestamp
 */
export const getVideoUrlWithTimestamp = (videoId, timestamp, platform = 'youtube') => {
  if (platform === 'vimeo') {
    return `https://vimeo.com/${videoId}#t=${timestamp}s`;
  } else {
    return `https://www.youtube.com/watch?v=${videoId}&t=${timestamp}`;
  }
};

/**
 * Get oEmbed URL for fetching video metadata
 * @param {string} videoId - Video ID (may include privacy hash for Vimeo)
 * @param {string} platform - Platform ('youtube' or 'vimeo')
 * @returns {string} oEmbed URL
 */
export const getOEmbedUrl = (videoId, platform = 'youtube') => {
  const videoUrl = getVideoUrl(videoId, platform);
  if (platform === 'vimeo') {
    return `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(videoUrl)}`;
  } else {
    return `https://www.youtube.com/oembed?url=${encodeURIComponent(videoUrl)}&format=json`;
  }
};

/**
 * Extract video ID from a URL based on platform
 * @param {string} url - Video URL
 * @param {string} platform - Platform ('youtube' or 'vimeo')
 * @returns {string} Video ID (may include privacy hash for Vimeo)
 */
export const extractVideoId = (url, platform = 'youtube') => {
  if (platform === 'vimeo') {
    // Handle formats like: https://vimeo.com/123456789 or https://vimeo.com/123456789/abcdef123
    const match = url.match(/vimeo\.com\/(\d+(?:\/[a-f0-9]+)?)/);
    return match ? match[1] : url;
  } else {
    // Handle YouTube formats
    if (url.includes('v=')) {
      return url.split('v=')[1].split('&')[0];
    } else if (url.includes('youtu.be/')) {
      return url.split('youtu.be/')[1].split('?')[0];
    }
    return url;
  }
};

/**
 * Extract the base video ID (without privacy hash) for Vimeo videos
 * @param {string} videoId - Video ID (may include privacy hash)
 * @returns {string} Base video ID (numeric only)
 */
export const getBaseVideoId = (videoId) => {
  if (typeof videoId === 'string' && videoId.includes('/')) {
    return videoId.split('/')[0];
  }
  return videoId;
};

/**
 * Get the privacy hash from a Vimeo video ID if present
 * @param {string} videoId - Video ID (may include privacy hash)
 * @returns {string|null} Privacy hash or null if not present
 */
export const getPrivacyHash = (videoId) => {
  if (typeof videoId === 'string' && videoId.includes('/')) {
    const parts = videoId.split('/');
    return parts.length > 1 ? parts[1] : null;
  }
  return null;
};

/**
 * Check if ReactPlayer supports the platform
 * @param {string} platform - Platform ('youtube' or 'vimeo')
 * @returns {boolean} Whether ReactPlayer supports this platform
 */
export const isReactPlayerSupported = (platform) => {
  return ['youtube', 'vimeo'].includes(platform);
}; 